import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CreateTestPlanRequest,
  TestPlan,
  TestPlanExecution,
  TestPlanReportDetails,
  TestPlanRun,
  UpdateTestPlanRequest,
} from '../models/test-plan.model';

@Injectable({ providedIn: 'root' })
export class TestPlanService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  private url(projectId: string, planId?: string): string {
    const root = `${this.base}/${projectId}/test-plans`;
    return planId ? `${root}/${encodeURIComponent(planId)}` : root;
  }

  list(projectId: string): Observable<TestPlan[]> {
    return this.http.get<TestPlan[]>(this.url(projectId));
  }

  getById(projectId: string, planId: string): Observable<TestPlan> {
    return this.http.get<TestPlan>(this.url(projectId, planId));
  }

  create(projectId: string, request: CreateTestPlanRequest): Observable<TestPlan> {
    return this.http.post<TestPlan>(this.url(projectId), request);
  }

  update(projectId: string, planId: string, request: UpdateTestPlanRequest): Observable<TestPlan> {
    return this.http.put<TestPlan>(this.url(projectId, planId), request);
  }

  delete(projectId: string, planId: string): Observable<void> {
    return this.http.delete<void>(this.url(projectId, planId));
  }

  archive(projectId: string, planId: string): Observable<void> {
    return this.http.post<void>(`${this.url(projectId, planId)}/archive`, null);
  }

  execute(projectId: string, planId: string): Observable<TestPlanExecution> {
    return this.http.post<TestPlanExecution>(`${this.url(projectId, planId)}/execute`, {});
  }

  getReportDetails(projectId: string, planId: string): Observable<TestPlanReportDetails[]> {
    return this.http.get<TestPlanReportDetails[]>(`${this.url(projectId, planId)}/report-details`);
  }

  getRuns(projectId: string, planId: string): Observable<TestPlanRun[]> {
    return this.http.get<TestPlanRun[]>(`${this.url(projectId, planId)}/runs`);
  }

  getExecution(projectId: string, planId: string): Observable<TestPlanExecution | null> {
    return this.http
      .get<TestPlanExecution | null>(`${this.url(projectId, planId)}/execution`, { observe: 'response' })
      .pipe(map((res) => (res.status === 204 ? null : (res.body as TestPlanExecution))));
  }
}
